import React, { useState } from "react";
import type { ProjectSnapshot } from "../services/projectService";

interface SnapshotDrawerProps {
  projectId: string | null;
  snapshots: ProjectSnapshot[];
  onSelectSnapshot: (snapshot: ProjectSnapshot) => void;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function SnapshotDrawer({
  projectId,
  snapshots,
  onSelectSnapshot,
}: SnapshotDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!projectId) return null;

  return (
    <>
      <button
        type="button"
        data-testid="snapshot-drawer-toggle"
        onClick={() => setIsOpen((v) => !v)}
        title="Version history"
        className="flex items-center gap-1.5 text-xs font-mono text-slate-500 hover:text-slate-300 transition-colors px-2 py-1 rounded border border-transparent hover:border-white/10"
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>History</span>
        {snapshots.length > 0 && (
          <span className="bg-white/10 text-slate-400 rounded px-1 tabular-nums">
            {snapshots.length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/30"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div
            data-testid="snapshot-drawer"
            className="fixed inset-y-0 right-0 z-40 w-72 bg-[#0d1117] border-l border-white/10 shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-xs font-semibold text-slate-300 uppercase tracking-widest">
                Version History
              </span>
              <button
                type="button"
                data-testid="snapshot-drawer-close"
                onClick={() => setIsOpen(false)}
                className="text-slate-500 hover:text-white transition-colors text-lg leading-none"
                aria-label="Close version history"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {snapshots.length === 0 ? (
                <p className="text-xs text-slate-600 italic px-1">
                  No snapshots yet — send a message to auto-save.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {[...snapshots].reverse().map((snap) => (
                    <li key={snap.version}>
                      <button
                        type="button"
                        data-testid={`snapshot-version-${snap.version}`}
                        onClick={() => {
                          onSelectSnapshot(snap);
                          setIsOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 rounded border border-white/5 bg-white/5 hover:bg-white/10 hover:border-cyan-500/20 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-cyan-400 font-mono font-semibold text-xs">
                            V{snap.version}
                          </span>
                          <span className="text-slate-600 text-[10px] group-hover:text-slate-400 transition-colors">
                            {snap.facets.length} card
                            {snap.facets.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          {snap.agent_leg}
                        </div>
                        <div className="text-[10px] text-slate-600 mt-0.5">
                          {fmtDate(snap.timestamp)}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
